/**
 * Task 29: Call Logs Tab Component
 * Displays and manages call history with real-time updates
 */

import { useState, useEffect } from 'react';
import { Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, Clock, Calendar, User, RefreshCw, Search, Filter, Download, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import axiosInstance from '../config/axiosConfig';
import QuickActionBar from '../components/shared/QuickActionBar'; // Task 33: Unified Quick-Action Layer

const CallLogsTab = ({ currentUser }) => {
  const [callLogs, setCallLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  });
  const [refreshing, setRefreshing] = useState(false);

  // Load call logs on component mount
  useEffect(() => {
    loadCallLogs();
  }, [pagination.page, statusFilter, dateFilter]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!refreshing) {
        loadCallLogs(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [refreshing]);

  const loadCallLogs = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      // Backend doesn't support pagination, fetch all logs
      const response = await axiosInstance.get('/call_logs');
      
      // Backend returns array directly
      const allCallLogs = Array.isArray(response.data) ? response.data : [];
      
      // Apply client-side filtering
      let filteredLogs = allCallLogs;
      if (statusFilter !== 'all') {
        filteredLogs = allCallLogs.filter(log => 
          log.call_status?.toLowerCase() === statusFilter.toLowerCase()
        );
      }
      
      // Apply client-side pagination
      const startIndex = (pagination.page - 1) * pagination.limit;
      const paginatedLogs = filteredLogs.slice(startIndex, startIndex + pagination.limit);
      
      setCallLogs(paginatedLogs);
      setPagination(prev => ({
        ...prev,
        total: filteredLogs.length,
        hasMore: startIndex + pagination.limit < filteredLogs.length
      }));
    } catch (err) {
      console.error('Failed to load call logs:', err);
      setError(err.response?.data?.detail || 'Failed to load call logs');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadCallLogs();
  };

  const handleCallCompleted = (callData) => {
    // Refresh call logs to include the completed call
    loadCallLogs(true);
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'connected':
      case 'completed':
        return <CheckCircle className="text-teal" size={16} />;
      case 'failed':
      case 'rejected':
        return <XCircle className="text-red-400" size={16} />;
      case 'missed':
        return <PhoneMissed className="text-yellow-400" size={16} />;
      case 'initiated':
        return <AlertCircle className="text-blue-400" size={16} />;
      default:
        return <Clock className="text-textLight/60" size={16} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'connected':
      case 'completed':
        return 'text-teal bg-teal/10 border-teal/30';
      case 'failed':
      case 'rejected':
        return 'text-red-400 bg-red-500/10 border-red-500/30';
      case 'missed':
        return 'text-yellow-400 bg-yellow-500/10 border-yellow-500/30';
      case 'initiated':
        return 'text-blue-400 bg-blue-500/10 border-blue-500/30';
      default:
        return 'text-textLight/60 bg-textLight/10 border-textLight/30';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const filteredLogs = callLogs.filter(log => {
    const searchLower = searchTerm.toLowerCase();
    return (
      log.candidate_name?.toLowerCase().includes(searchLower) ||
      log.candidate_phone?.includes(searchLower) ||
      log.recruiter_name?.toLowerCase().includes(searchLower) ||
      log.call_id?.toLowerCase().includes(searchLower)
    );
  });

  const handleExport = async () => {
    try {
      // This would implement export functionality
      console.log('Export call logs');
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  if (loading && callLogs.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="animate-spin text-teal mr-3" size={20} />
        <span className="text-textLight">Loading call logs...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-textLight">Call History</h3>
          <p className="text-textLight/60 text-sm mt-1">
            {pagination.total} call{pagination.total !== 1 ? 's' : ''} recorded
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-navy-light/50 text-textLight/70 hover:bg-navy-light/70 hover:text-textLight transition-all duration-200"
          >
            <Download size={16} />
            <span className="text-sm">Export</span>
          </button>
          
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-teal/20 text-teal border border-teal/30 hover:bg-teal/30 transition-all duration-200 disabled:opacity-50"
          >
            <RefreshCw className={refreshing ? 'animate-spin' : ''} size={16} />
            <span className="text-sm">Refresh</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-textLight/40" size={16} />
          <input
            type="text"
            placeholder="Search by candidate, phone, or call ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-navy-light/50 border border-navy-light/30 text-textLight placeholder-textLight/40 focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal/50"
          />
        </div>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-navy-light/50 border border-navy-light/30 text-textLight focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal/50"
        >
          <option value="all">All Status</option>
          <option value="completed">Completed</option>
          <option value="connected">Connected</option>
          <option value="missed">Missed</option>
          <option value="failed">Failed</option>
          <option value="initiated">Initiated</option>
        </select>

        {/* Date Filter */}
        <select
          value={dateFilter}
          onChange={(e) => setDateFilter(e.target.value)}
          className="px-4 py-2 rounded-lg bg-navy-light/50 border border-navy-light/30 text-textLight focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal/50"
        >
          <option value="all">All Time</option>
          <option value="today">Today</option>
          <option value="week">This Week</option>
          <option value="month">This Month</option>
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center space-x-3 p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <XCircle className="text-red-400" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Call Logs Table */}
      <div className="rounded-lg border border-navy-light/30 overflow-hidden">
        {filteredLogs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Phone className="text-textLight/40 mb-3" size={48} />
            <h4 className="text-textLight font-medium mb-2">No call logs found</h4>
            <p className="text-textLight/60 text-sm">
              {searchTerm ? 'Try adjusting your search terms' : 'Start making calls to see them here'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-navy-light/30 border-b border-navy-light/30">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Candidate
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-textLight/60 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-navy-light/30">
                {filteredLogs.map((log) => (
                  <tr key={log.id} className="hover:bg-navy-light/20 transition-colors duration-150">
                    <td className="px-4 py-4">
                      <div>
                        <div className="font-medium text-textLight">{log.candidate_name || 'Unknown'}</div>
                        <div className="text-xs text-textLight/60">ID: {log.candidate_id}</div>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-textLight">{log.candidate_phone || 'N/A'}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(log.call_status)}
                        <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(log.call_status)}`}>
                          {log.call_status || 'Unknown'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-textLight">{formatDuration(log.duration_seconds)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-textLight text-sm">{formatDate(log.initiated_at)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center space-x-2">
                        <QuickActionBar
                          candidate={{
                            id: log.candidate_id,
                            name: log.candidate_name,
                            phone: log.candidate_phone
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-textLight/60">
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="px-3 py-1 rounded bg-navy-light/50 text-textLight/70 hover:bg-navy-light/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Previous
            </button>
            
            <span className="px-3 py-1 text-textLight">
              {pagination.page}
            </span>
            
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={!pagination.hasMore}
              className="px-3 py-1 rounded bg-navy-light/50 text-textLight/70 hover:bg-navy-light/70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CallLogsTab;
